import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  AlertCircle,
  BookMarked,
  CalendarClock,
  CheckCircle2,
  CircleSlash,
  Clock3,
  Pause,
  Play,
  Plus,
  RefreshCw,
  Search,
  ShieldCheck,
  Trash2,
  Upload,
  X,
  XCircle,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useDebounce } from '../hooks/useDebounce';
import { useVisiblePolling } from '../hooks/useVisiblePolling';
import { useNowSeconds } from '../hooks/useNowSeconds';
import { useScheduleTargets } from '../hooks/useScheduleTargets';
import { useTourNavigation } from '../hooks/useTourNavigation';
import { PageHeader } from '../components/ui/PageHeader';
import { StatTile } from '../components/ui/StatTile';
import { EmptyState } from '../components/ui/EmptyState';
import { PanelTour } from '../components/ui/PanelTour';
import { SkeletonLoader } from '../components/SkeletonLoader';
import { ConfirmationModal } from '../components/ConfirmationModal';
import { ScheduleTimeline } from '../components/scheduledcommands/ScheduleTimeline';
import { ScheduleTable } from '../components/scheduledcommands/ScheduleTable';
import { RunNowModal } from '../components/scheduledcommands/RunNowModal';
import { PlayerSendModal } from '../components/scheduledcommands/PlayerSendModal';
import { TellrawLibraryPanel } from '../components/scheduledcommands/TellrawLibraryPanel';
import { scheduledCommandsPanelTour } from '../config/panelTours';
import { scheduledCommandsApi } from '../services/scheduledCommandsApi';
import type { BulkAction, BulkResult, Schedule, ScheduleListResponse } from '../types/scheduledCommands';
import { getScheduledCommandPerms } from '../utils/scheduledCommandPermissions';
import {
  isFailing,
  narrowToPlayer,
  reachOf,
  scheduleDisplayName,
  scheduleState,
  targetKey,
  targetLabel,
} from '../utils/scheduledCommands';
import { formatInterval } from '../utils/interval';
import { cn } from '../utils/cn';

type Tab = 'schedules' | 'library';
type StateFilter = 'active' | 'paused' | 'not_started' | 'failing' | null;
type Sort = 'next' | 'name' | 'changed';

interface Notice {
  tone: 'success' | 'info' | 'danger';
  text: string;
}

const ALL = '';

/**
 * Scheduled commands: the console commands Mr. Roboto sends to servers on a
 * timer, and the tellraw library they are written from.
 *
 * The bot owns the schedules and fires them; this page reads them from the bot
 * through argus-backend and changes them the same way, so a schedule edited
 * here is the one that runs. Nothing here fires a command except the explicit
 * Run now and Send to player actions.
 */
export function ScheduledCommandsView() {
  useDocumentTitle('Scheduled Commands');
  const { identity } = useAuth();
  const isDev = import.meta.env.DEV;
  const perms = useMemo(() => getScheduledCommandPerms(identity, isDev), [identity, isDev]);
  const { tourNavigation } = useTourNavigation();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const tab: Tab = params.get('tab') === 'library' ? 'library' : 'schedules';

  const now = useNowSeconds(15_000);
  const targets = useScheduleTargets();

  const [data, setData] = useState<ScheduleListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<Notice | null>(null);

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 200);
  const [targetFilter, setTargetFilter] = useState(ALL);
  const [stateFilter, setStateFilter] = useState<StateFilter>(null);
  const [sort, setSort] = useState<Sort>('next');

  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [toggling, setToggling] = useState<Set<number>>(new Set());
  const [highlightId, setHighlightId] = useState<number | null>(null);
  const [focusId, setFocusId] = useState<number | null>(null);

  const [running, setRunning] = useState<Schedule | null>(null);
  const [sending, setSending] = useState<Schedule | null>(null);
  const [deleting, setDeleting] = useState<Schedule | null>(null);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [busy, setBusy] = useState(false);
  const [rebalancing, setRebalancing] = useState(false);

  const load = useCallback(async (quiet = false) => {
    if (quiet) setRefreshing(true);
    else setLoading(true);
    try {
      const next = await scheduledCommandsApi.list();
      setData(next);
      setError(null);
      // Drop selections of schedules that no longer exist.
      setSelected((prev) => {
        const ids = new Set(next.schedules.map((s) => s.id));
        const kept = [...prev].filter((id) => ids.has(id));
        return kept.length === prev.size ? prev : new Set(kept);
      });
    } catch (e) {
      if (!quiet) setError(e instanceof Error ? e.message : 'Scheduled commands could not be loaded.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    if (perms.canView) void load();
  }, [load, perms.canView]);

  // Keep last runs and next runs current while the page is open.
  useVisiblePolling(() => {
    if (tab === 'schedules' && perms.canView) void load(true);
  }, 30_000);

  const schedules = useMemo(() => data?.schedules ?? [], [data]);
  const names = useMemo(() => new Map(schedules.map((s) => [s.id, scheduleDisplayName(s)])), [schedules]);

  const stats = useMemo(() => {
    let active = 0;
    let paused = 0;
    let ended = 0;
    let notStarted = 0;
    let failing = 0;
    for (const s of schedules) {
      const state = scheduleState(s, now);
      if (state === 'active') active += 1;
      else if (state === 'paused') paused += 1;
      else if (state === 'ended') ended += 1;
      else notStarted += 1;
      if (isFailing(s.last_run)) failing += 1;
    }
    return { active, paused, ended, notStarted, failing };
  }, [schedules, now]);

  const targetOptions = useMemo(() => {
    const seen = new Map<string, string>();
    for (const s of schedules) for (const t of s.targets) seen.set(targetKey(t), t.type === 'server' ? t.address : targetLabel(t));
    return [...seen.entries()].sort((a, b) => (a[0] === 'all' ? -1 : b[0] === 'all' ? 1 : a[1].localeCompare(b[1])));
  }, [schedules]);

  const filtered = useMemo(() => {
    const query = debouncedSearch.trim().toLowerCase();
    const rows = schedules.filter((s) => {
      if (targetFilter && !s.targets.some((t) => targetKey(t) === targetFilter)) return false;
      if (stateFilter) {
        const state = scheduleState(s, now);
        if (stateFilter === 'failing' && !isFailing(s.last_run)) return false;
        if (stateFilter === 'paused' && state !== 'paused' && state !== 'ended') return false;
        if ((stateFilter === 'active' || stateFilter === 'not_started') && state !== stateFilter) return false;
      }
      if (!query) return true;
      return (
        scheduleDisplayName(s).toLowerCase().includes(query) ||
        s.command.toLowerCase().includes(query) ||
        s.targets.some((t) => targetLabel(t).toLowerCase().includes(query))
      );
    });
    const rank = (s: Schedule) => ({ active: 0, not_started: 1, paused: 2, ended: 3 })[scheduleState(s, now)];
    return rows.sort((a, b) => {
      if (sort === 'name') return scheduleDisplayName(a).localeCompare(scheduleDisplayName(b));
      if (sort === 'changed') return (b.updated_at ?? b.created_at) - (a.updated_at ?? a.created_at);
      return rank(a) - rank(b) || (a.next_run_at ?? Infinity) - (b.next_run_at ?? Infinity) || a.id - b.id;
    });
  }, [schedules, debouncedSearch, targetFilter, stateFilter, sort, now]);

  const filtersActive = !!(debouncedSearch.trim() || targetFilter || stateFilter);
  const clearFilters = () => {
    setSearch('');
    setTargetFilter(ALL);
    setStateFilter(null);
  };

  const reachCount = (s: Schedule) => (targets.catalog ? reachOf(s.targets, targets.catalog).servers.length : null);

  const setTab = (next: Tab) => {
    const nextParams = new URLSearchParams(params);
    if (next === 'library') nextParams.set('tab', 'library');
    else nextParams.delete('tab');
    setParams(nextParams, { replace: true });
  };

  // ── Actions ──────────────────────────────────────────────────────────────

  const toggle = async (schedule: Schedule, enabled: boolean) => {
    setToggling((prev) => new Set(prev).add(schedule.id));
    // Optimistic: the switch moves at once, and moves back if the bot refuses.
    setData((prev) => prev && { ...prev, schedules: prev.schedules.map((s) => (s.id === schedule.id ? { ...s, enabled } : s)) });
    try {
      const updated = await scheduledCommandsApi.update(schedule.id, { enabled });
      setData((prev) => prev && { ...prev, schedules: prev.schedules.map((s) => (s.id === updated.id ? updated : s)) });
      // Resuming re-spreads every schedule, so everyone else's next run may have moved too.
      if (enabled) void load(true);
    } catch (e) {
      setData((prev) => prev && { ...prev, schedules: prev.schedules.map((s) => (s.id === schedule.id ? schedule : s)) });
      setNotice({ tone: 'danger', text: `${scheduleDisplayName(schedule)} was not ${enabled ? 'resumed' : 'paused'}: ${e instanceof Error ? e.message : 'unknown error'}` });
    } finally {
      setToggling((prev) => {
        const next = new Set(prev);
        next.delete(schedule.id);
        return next;
      });
    }
  };

  const rebalance = async () => {
    setRebalancing(true);
    try {
      const result = await scheduledCommandsApi.rebalance();
      await load(true);
      const closest = result.spacing.closest_gap_seconds;
      setNotice({
        tone: 'success',
        text: `Spread ${result.schedules.length} schedule${result.schedules.length === 1 ? '' : 's'} out again.${
          closest !== null ? ` The closest two runs are now ${formatInterval(Math.round(closest))} apart.` : ''
        }`,
      });
    } catch (e) {
      setNotice({ tone: 'danger', text: e instanceof Error ? e.message : 'The schedules could not be rebalanced.' });
    } finally {
      setRebalancing(false);
    }
  };

  const describeBulk = (action: BulkAction, result: BulkResult): Notice => {
    const verb = action === 'pause' ? 'Paused' : action === 'resume' ? 'Resumed' : 'Deleted';
    const parts = [`${verb} ${result.changed.length} schedule${result.changed.length === 1 ? '' : 's'}.`];
    if (result.unchanged.length) parts.push(`${result.unchanged.length} already ${action === 'pause' ? 'paused' : 'running'}.`);
    if (result.missing.length) parts.push(`${result.missing.length} no longer existed.`);
    for (const refusal of result.refused) parts.push(`${names.get(refusal.id) ?? `#${refusal.id}`}: ${refusal.error}`);
    return { tone: result.refused.length ? 'info' : 'success', text: parts.join(' ') };
  };

  const bulk = async (action: BulkAction) => {
    const ids = [...selected];
    if (!ids.length) return;
    setBusy(true);
    try {
      const result = await scheduledCommandsApi.bulk(ids, action);
      setNotice(describeBulk(action, result));
      setSelected(new Set());
      await load(true);
    } catch (e) {
      setNotice({ tone: 'danger', text: e instanceof Error ? e.message : 'That did not go through.' });
    } finally {
      setBusy(false);
      setBulkDeleting(false);
    }
  };

  const remove = async (schedule: Schedule) => {
    setBusy(true);
    try {
      await scheduledCommandsApi.remove(schedule.id);
      setNotice({ tone: 'success', text: `Deleted ${scheduleDisplayName(schedule)}. It will not run again.` });
      await load(true);
    } catch (e) {
      setNotice({ tone: 'danger', text: e instanceof Error ? e.message : 'The schedule could not be deleted.' });
    } finally {
      setBusy(false);
      setDeleting(null);
    }
  };

  const focusSchedule = (id: number) => {
    clearFilters();
    setFocusId(id);
    window.setTimeout(() => setFocusId((current) => (current === id ? null : current)), 2500);
  };

  // ── Render ───────────────────────────────────────────────────────────────

  const header = (
    <PageHeader
      eyebrow="Network Config"
      title="Scheduled Commands"
      icon={CalendarClock}
      description="Console commands Mr. Roboto sends to servers on a timer: announcements, reminders, routine upkeep. Changes take effect on its next tick."
      actions={
        perms.canView && (
          <div className="flex items-center gap-2">
            <PanelTour
              tourId="scheduled-commands"
              title="Scheduled Commands"
              steps={scheduledCommandsPanelTour(perms.canWrite)}
              enabled={perms.canView && !loading && tab === 'schedules'}
              {...tourNavigation}
            />
            {tab === 'schedules' ? (
              <>
                <button type="button" onClick={() => void load(true)} className="glass-button" title="Refresh" aria-label="Refresh">
                  <RefreshCw className={cn('w-4 h-4', refreshing && 'animate-spin')} />
                  <span className="hidden sm:inline">Refresh</span>
                </button>
                {perms.canWrite && (
                  <button type="button" onClick={() => navigate('/scheduled-commands/new')} className="btn-primary" data-tour="schedules-new">
                    <Plus className="w-4 h-4" />
                    New schedule
                  </button>
                )}
              </>
            ) : (
              perms.canWrite && (
                <>
                  <button type="button" onClick={() => navigate('/scheduled-commands/library/new?import=1')} className="glass-button">
                    <Upload className="w-4 h-4" />
                    <span className="hidden sm:inline">Import</span>
                  </button>
                  <button type="button" onClick={() => navigate('/scheduled-commands/library/new')} className="btn-primary">
                    <Plus className="w-4 h-4" />
                    New tellraw
                  </button>
                </>
              )
            )}
          </div>
        )
      }
    >
      {perms.canView && (
        <div className="flex flex-col gap-2">
          <div className="segmented max-w-full self-start" role="tablist" aria-label="Scheduled commands sections">
            <button type="button" role="tab" aria-selected={tab === 'schedules'} className="segmented-item" data-active={tab === 'schedules'} onClick={() => setTab('schedules')}>
              <CalendarClock className="w-4 h-4" />
              Schedules
              {data && <span className="badge badge-neutral">{schedules.length}</span>}
            </button>
            <button type="button" role="tab" aria-selected={tab === 'library'} className="segmented-item" data-active={tab === 'library'} onClick={() => setTab('library')}>
              <BookMarked className="w-4 h-4" />
              Tellraw library
            </button>
          </div>

          {tab === 'schedules' && (
            <div className="flex flex-col md:flex-row md:items-center gap-2" data-tour="schedules-filters">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-glass-400" />
                <input
                  className="glass-input w-full pl-9"
                  placeholder="Search names, commands and targets…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  aria-label="Search schedules"
                />
              </div>
              <select className="glass-select" value={targetFilter} onChange={(e) => setTargetFilter(e.target.value)} aria-label="Filter by target">
                <option value={ALL}>Any target</option>
                {targetOptions.map(([key, label]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
              <select className="glass-select" value={sort} onChange={(e) => setSort(e.target.value as Sort)} aria-label="Sort">
                <option value="next">Next to run first</option>
                <option value="name">By name</option>
                <option value="changed">Recently changed</option>
              </select>
              <span className="text-xs text-glass-400 tabular-nums md:ml-auto">
                {filtered.length} of {schedules.length}
              </span>
            </div>
          )}
        </div>
      )}
    </PageHeader>
  );

  if (!perms.canView) {
    return (
      <div className="space-y-6">
        {header}
        <EmptyState
          tone="warning"
          icon={ShieldCheck}
          title="You don't have permission to view scheduled commands"
          description={<>Ask an admin for the <code className="font-mono text-xs">argus.scheduledcommands.read</code> permission.</>}
        />
      </div>
    );
  }

  if (tab === 'library') {
    return (
      <div className="space-y-6">
        {header}
        <TellrawLibraryPanel canWrite={perms.canWrite} />
      </div>
    );
  }

  const selectedCount = selected.size;

  return (
    <div className="space-y-6">
      {header}

      {notice && (
        <div className={cn('alert', notice.tone === 'danger' ? 'alert-danger' : notice.tone === 'info' ? 'alert-info' : 'alert-success')} role="status">
          {notice.tone === 'danger' ? <AlertCircle className="w-4 h-4 shrink-0" /> : <CheckCircle2 className="w-4 h-4 shrink-0" />}
          <span className="flex-1">{notice.text}</span>
          <button type="button" onClick={() => setNotice(null)} className="btn-ghost ml-auto shrink-0">Dismiss</button>
        </div>
      )}

      {loading && !data ? (
        <>
          <div className="glass-card p-4 md:p-5 space-y-3">
            <SkeletonLoader className="h-5 w-48" />
            <SkeletonLoader className="h-12" />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {Array.from({ length: 4 }).map((_, i) => <SkeletonLoader key={i} className="h-20" />)}
          </div>
          <div className="glass-card p-4 space-y-3">
            {Array.from({ length: 6 }).map((_, i) => <SkeletonLoader key={i} className="h-12" />)}
          </div>
        </>
      ) : error && !data ? (
        <EmptyState
          tone="danger"
          icon={AlertCircle}
          title="Scheduled commands could not be loaded"
          description={error}
          action={
            <button type="button" onClick={() => void load()} className="glass-button">
              <RefreshCw className="w-4 h-4" />
              Retry
            </button>
          }
        />
      ) : schedules.length === 0 ? (
        <EmptyState
          icon={CalendarClock}
          title="No scheduled commands yet"
          description="A schedule sends a console command on a timer, to every server, a cluster, or chosen servers. Announcements, vote reminders and routine upkeep usually live here."
          action={
            perms.canWrite && (
              <button type="button" onClick={() => navigate('/scheduled-commands/new')} className="btn-primary">
                <Plus className="w-4 h-4" />
                New schedule
              </button>
            )
          }
        />
      ) : (
        <>
          <ScheduleTimeline
            schedules={schedules}
            names={names}
            nowSec={now}
            highlightId={highlightId}
            onSelect={focusSchedule}
            canRebalance={perms.canWrite}
            rebalancing={rebalancing}
            onRebalance={() => void rebalance()}
            refreshing={refreshing}
          />

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatTile
              label="Active"
              value={stats.active}
              icon={Play}
              tone="success"
              active={stateFilter === 'active'}
              onClick={() => setStateFilter(stateFilter === 'active' ? null : 'active')}
              hint="on their timer"
            />
            <StatTile
              label="Paused"
              value={stats.paused + stats.ended}
              icon={Pause}
              active={stateFilter === 'paused'}
              onClick={() => setStateFilter(stateFilter === 'paused' ? null : 'paused')}
              hint={stats.ended ? `${stats.ended} ended by date` : 'switched off'}
            />
            <StatTile
              label="Not started"
              value={stats.notStarted}
              icon={Clock3}
              tone="info"
              active={stateFilter === 'not_started'}
              onClick={() => setStateFilter(stateFilter === 'not_started' ? null : 'not_started')}
              hint="waiting to start"
            />
            <StatTile
              label="Failing"
              value={stats.failing}
              icon={XCircle}
              tone={stats.failing > 0 ? 'danger' : 'neutral'}
              active={stateFilter === 'failing'}
              onClick={() => setStateFilter(stateFilter === 'failing' ? null : 'failing')}
              hint={stats.failing > 0 ? 'last run failed' : 'none failing'}
            />
          </div>

          {perms.canWrite && selectedCount > 0 && (
            <div className="glass-card sticky top-2 z-20 flex flex-wrap items-center gap-2 px-3 py-2" role="region" aria-label="Selected schedules">
              <span className="text-sm text-white tabular-nums mr-1">{selectedCount} selected</span>
              <button type="button" onClick={() => void bulk('pause')} className="glass-button glass-button-sm" disabled={busy}>
                <Pause className="w-4 h-4" />
                Pause
              </button>
              <button type="button" onClick={() => void bulk('resume')} className="glass-button glass-button-sm" disabled={busy}>
                <Play className="w-4 h-4" />
                Resume
              </button>
              <button type="button" onClick={() => setBulkDeleting(true)} className="btn-danger glass-button-sm" disabled={busy}>
                <Trash2 className="w-4 h-4" />
                Delete
              </button>
              <button type="button" onClick={() => setSelected(new Set())} className="btn-ghost ml-auto" disabled={busy}>
                <X className="w-4 h-4" />
                Clear
              </button>
            </div>
          )}

          {filtered.length === 0 ? (
            <EmptyState
              icon={CircleSlash}
              title="Nothing matches those filters"
              description="Try a broader search or clear a filter."
              action={
                <button type="button" onClick={clearFilters} className="glass-button-secondary">
                  Clear filters
                </button>
              }
            />
          ) : (
            <ScheduleTable
              schedules={filtered}
              nowSec={now}
              canWrite={perms.canWrite}
              selected={selected}
              onSelectedChange={setSelected}
              toggling={toggling}
              focusId={focusId}
              onHover={setHighlightId}
              onToggle={(s, enabled) => void toggle(s, enabled)}
              onRun={setRunning}
              onSendToPlayer={setSending}
              onDuplicate={(s) => navigate(`/scheduled-commands/new?from=${s.id}`)}
              onDelete={setDeleting}
            />
          )}
          {filtersActive && filtered.length > 0 && (
            <p className="text-xs text-glass-500">
              Filters are on.{' '}
              <button type="button" onClick={clearFilters} className="link">Show every schedule</button>
            </p>
          )}
        </>
      )}

      {running && (
        <RunNowModal schedule={running} reachCount={reachCount(running)} onClose={() => setRunning(null)} onRan={() => void load(true)} />
      )}

      {sending && (
        <PlayerSendModal
          title={`Send “${scheduleDisplayName(sending)}” to one player`}
          subtitle="Every @a in the command becomes their name."
          defaultPlayer={identity?.raw?.custom?.username ?? null}
          blockedReason={/@a/.test(sending.command) ? null : 'This command does not target @a, so it cannot be narrowed to one player.'}
          renderPreview={(player) => (
            <pre className="subcard p-3 max-h-40 overflow-auto font-mono text-xs text-glass-300 whitespace-pre-wrap break-all">
              {narrowToPlayer(sending.command, player).command}
            </pre>
          )}
          send={(player) => scheduledCommandsApi.run(sending.id, player)}
          onClose={() => setSending(null)}
          onSent={() => void load(true)}
        />
      )}

      <ConfirmationModal
        isOpen={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={() => deleting && void remove(deleting)}
        title={deleting ? `Delete ${scheduleDisplayName(deleting)}?` : 'Delete schedule?'}
        message={
          deleting
            ? `It stops running${reachCount(deleting) !== null ? ` on ${reachCount(deleting)} server${reachCount(deleting) === 1 ? '' : 's'}` : ''} and its run history goes with it. This cannot be undone.`
            : ''
        }
        confirmText="Delete schedule"
        isLoading={busy}
      />

      <ConfirmationModal
        isOpen={bulkDeleting}
        onClose={() => setBulkDeleting(false)}
        onConfirm={() => void bulk('delete')}
        title={`Delete ${selectedCount} schedule${selectedCount === 1 ? '' : 's'}?`}
        message={`${[...selected].map((id) => names.get(id) ?? `#${id}`).slice(0, 6).join(', ')}${selectedCount > 6 ? ` and ${selectedCount - 6} more` : ''} will stop running. This cannot be undone.`}
        confirmText={`Delete ${selectedCount}`}
        isLoading={busy}
      />
    </div>
  );
}
